import type { MaterialItemsSsrmRow } from "~produce/production/materialItems/helpers/materialItemsSsrmRow";
import type { MaterialPriceDraft } from "~produce/production/materialItems/helpers/materialPriceDraft";
import type { MaterialPricingWorklistScope } from "~produce/production/materialItems/helpers/materialPricingWorklist";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { MaterialItemDocument } from "~/gql/graphqlBase.generated";
import tenantChanged from "~/store/tenantChanged";
import { DEFAULT_PRICING_WORKLIST_SCOPE } from "~produce/production/materialItems/helpers/materialPricingWorklist";

export interface MaterialPriceWriteReceipts {
	priceRevisions: Record<string, number>;
	purchasingRevision: number;
}

export type MaterialPricingWorklistState = "EXHAUSTED" | "LOADING" | "READY";

export interface MaterialPricingContinuation {
	materialItemId: string;
	worklist: MaterialPricingWorklistState;
}

export interface MaterialPriceWriteReceiptRecord {
	materialItemId: string;
	priceId: string;
	priceRevision: number;
	purchasingRevision: number;
}

interface MaterialSetupState {
	countsTabRequest?: string;
	formModalOpen: boolean;
	priceDraft?: MaterialPriceDraft;
	priceReceipts: Record<string, MaterialPriceWriteReceipts>;
	pricingContinuation?: MaterialPricingContinuation;
	pricingRefreshing: boolean;
	pricingWorkspaceOpen: boolean;
	pricingWorklistScope: MaterialPricingWorklistScope;
	pricingWorkspaceRow?: MaterialItemsSsrmRow;
	selectedMaterialItem?: MaterialItemDocument;
}

const initialState: MaterialSetupState = {
	formModalOpen: false,
	priceReceipts: {},
	pricingRefreshing: false,
	pricingWorkspaceOpen: false,
	pricingWorklistScope: DEFAULT_PRICING_WORKLIST_SCOPE
};

const materialSetupSlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(tenantChanged, (state) => {
			state.countsTabRequest = undefined;
			state.priceDraft = undefined;
			state.priceReceipts = {};
			state.pricingContinuation = undefined;
			state.pricingWorkspaceRow = undefined;
			state.selectedMaterialItem = undefined;
		});
	},
	initialState,
	name: "materialSetup",
	reducers: {
		clearCountsTabRequest: (state) => {
			state.countsTabRequest = undefined;
		},
		closeMaterialItemFormModal: (state) => {
			state.formModalOpen = false;
		},
		closePriceDraft: (state) => {
			state.priceDraft = undefined;
		},
		closePricingWorkspace: (state) => {
			state.pricingWorkspaceOpen = false;
		},
		endPricingContinuation: (state) => {
			state.pricingContinuation = undefined;
		},
		markPricingWorklistExhausted: (state) => {
			if (state.pricingContinuation) state.pricingContinuation.worklist = "EXHAUSTED";
		},
		markPricingWorklistLoading: (state) => {
			if (state.pricingContinuation) state.pricingContinuation.worklist = "LOADING";
		},
		openPriceDraft: (state, action: PayloadAction<MaterialPriceDraft>) => {
			state.priceDraft = action.payload;
		},
		openPricingWorkspace: (state) => {
			state.pricingWorkspaceOpen = true;
		},
		openMaterialItemFormModal: (state) => {
			state.formModalOpen = true;
		},
		recordPriceWriteReceipt: (state, action: PayloadAction<MaterialPriceWriteReceiptRecord>) => {
			const { materialItemId, priceId, priceRevision, purchasingRevision } = action.payload;
			const written = state.priceReceipts[materialItemId];
			state.priceReceipts[materialItemId] = {
				priceRevisions: { ...written?.priceRevisions, [priceId]: priceRevision },
				purchasingRevision: Math.max(written?.purchasingRevision ?? 0, purchasingRevision)
			};
		},
		requestCountsTab: (state, action: PayloadAction<string>) => {
			state.countsTabRequest = action.payload;
		},
		setPricingRefreshing: (state, action: PayloadAction<boolean>) => {
			state.pricingRefreshing = action.payload;
		},
		setPricingWorklistScope: (state, action: PayloadAction<MaterialPricingWorklistScope>) => {
			state.pricingWorklistScope = action.payload;
		},
		setPricingWorkspaceRow: (state, action: PayloadAction<MaterialItemsSsrmRow | undefined>) => {
			state.pricingWorkspaceRow = action.payload;
		},
		setSelectedMaterialItem: (state, action: PayloadAction<MaterialItemDocument | undefined>) => {
			state.selectedMaterialItem = action.payload;
		},
		startPricingContinuation: (state, action: PayloadAction<string>) => {
			state.pricingContinuation = { materialItemId: action.payload, worklist: "READY" };
		},
		updatePriceDraft: (state, action: PayloadAction<Partial<MaterialPriceDraft>>) => {
			if (!state.priceDraft) return;
			state.priceDraft = { ...state.priceDraft, ...action.payload };
		}
	}
});

export const {
	clearCountsTabRequest,
	closeMaterialItemFormModal,
	closePriceDraft,
	closePricingWorkspace,
	endPricingContinuation,
	markPricingWorklistExhausted,
	markPricingWorklistLoading,
	openMaterialItemFormModal,
	openPriceDraft,
	openPricingWorkspace,
	recordPriceWriteReceipt,
	requestCountsTab,
	setPricingRefreshing,
	setPricingWorklistScope,
	setPricingWorkspaceRow,
	setSelectedMaterialItem,
	startPricingContinuation,
	updatePriceDraft
} = materialSetupSlice.actions;

export default materialSetupSlice.reducer;
